import { authFetch } from '@/utils/auth-fetch';

/**
 * Webhook delivery — client for backend-merged/routes/webhook-delivery.js (issue #7222; vault PRD
 * webhook delivery PRD rev. 5.2). The server never returns the credential value: the header name and
 * its last 4 characters only.
 */

export type WebhookStatus = 'not_enabled' | 'active' | 'paused' | 'disabled';
export type PauseReason = 'user' | 'auth_rejected' | 'circuit_open';
export type CallStatus = 'pending' | 'in_flight' | 'delivered' | 'held' | 'skipped' | 'failed' | 'superseded' | 'disabled';
/** The delivery log's filters (PRD "Delivery log": failed, held, skipped). */
export type LogFilter = 'all' | 'failed' | 'held' | 'skipped' | 'pending' | 'delivered' | 'closed';
export type RunStatus = 'queued' | 'running' | 'failed_retryable' | 'completed' | 'failed';

export interface WebhookLimits {
  fullSyncMinIntervalHours: number;
  deltaIntervalMinutes: number;
  maxParallel: { min: number; max: number; default: number };
  maxPerMinute: { min: number; max: number; default: number };
  /** rev. 5.7: the endpoint's response timeout (the default an Enable gives when the store has none) */
  requestTimeoutSeconds: number;
  /** rev. 5.7: the bounds of the setting */
  requestTimeoutMinSeconds: number;
  requestTimeoutMaxSeconds: number;
  maxRequestBytes: number;
  attemptsRetentionDays: number;
  // PRD rev. 5.3 (#7255): what the integration guide shows, read from WEBHOOK_RULES by the backend
  circuitConsecutiveFailures: number;
  circuitWindowSeconds: number;
  circuitFailureRatio: number;
  circuitMinSamples: number;
  probeDelaysMinutes: number[];
  probeSteadyMinutes: number;
  circuitPauseHours: number;
  retryAfterMaxSeconds: number;
  /** rev. 5.8: a 5xx to one product this many times, while others are accepted, is final (guide part 3) */
  perProductEndpointFailureLimit: number;
  shapeVersion: number;
  auth: { minSecretChars: number; maskSuffixChars: number; maskSuffixMinChars: number };
}

/** The payload contract the dispatcher validates with (rev. 5.4: "Schema 1.0"): its version and pinned SHA-256. */
export interface WebhookSchemaInfo {
  version: string;
  sha256: string;
}

/** GET /webhook-delivery/sample-identity (rev. 5.4): the sample product's sku, item group, store and site id, as its headers carry them. */
export interface WebhookSampleIdentity {
  sku: string;
  itemGroupId: string;
  store: string;
  siteId: string;
  /** rev. 5.6: X-PageMind-Granularity of the store's endpoint (model = canonicals only), product when it has none */
  granularity: WebhookGranularity;
}

/** rev. 5.6 part 1: what one call stands for (X-PageMind-Granularity). */
export type WebhookGranularity = 'model' | 'product';

export interface FullExportRun {
  id: string;
  status: RunStatus;
  runSeq: number;
  /** products queued by the full export so far */
  queued: number;
  queuingDone: boolean;
  /** the run's outcome ledger (PRD "Accounting"): captured = delivered + held + skipped + failed + disabled + pending */
  captured: number;
  delivered: number;
  held: number;
  skipped: number;
  failed: number;
  disabled: number;
  pending: number;
  error: string | null;
  requestedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface WebhookDelivery {
  status: WebhookStatus;
  url?: string;
  auth?: { headerName: string; masked: string; keyVersion: number } | null; // masked by the backend: never the whole secret
  gzip?: boolean;
  /** rev. 5.6 part 1: the Enable form offers "Only send canonicals" (the store's canonical aggregator is enabled) */
  canonicalsAvailable?: boolean;
  /** rev. 5.6 part 1: one call per model (canonicals only) instead of one per product; set at Enable, immutable */
  canonicalsOnly?: boolean;
  pace?: { maxParallel: number; maxPerMinute: number };
  /** rev. 5.7: how long we wait for the endpoint to answer each call (seconds); applies to the next claimed call */
  requestTimeoutSeconds?: number;
  pause?: { reason: PauseReason; detail: string | null; at: string | null } | null;
  enabledAt?: string | null;
  disabledAt?: string | null;
  queue?: { pending: number; waitingRetry: number; inFlight: number; held: number; failed: number; estimatedMinutesToEmpty: number };
  lastSuccessAt?: string | null;
  failuresLast24h?: number;
  nextRoundAt?: string | null;
  lastRound?: { id: string; status: RunStatus; runSeq: number; products: number; error: string | null; createdAt: string | null; finishedAt: string | null } | null;
  circuit?: { state: 'closed' | 'open'; openedAt: string | null; nextProbeAt: string | null; consecutiveFailures: number };
  retryAfterUntil?: string | null;
  fullExport?: { requestedAt: string | null; waiting: boolean; allowedAt: string | null; run: FullExportRun | null };
  held?: { total: number; groups: { path: string; count: number; schemaRevision: string | null }[]; schemaRevision: string };
  lastTest?: {
    deliveryId: string; productId: string; status: CallStatus; httpStatus: number | null; durationMs: number | null;
    response: string | null; outcome: string | null; error: string | null; at: string | null;
    /** a call of this test is out right now; httpStatus..at are then the previous COMPLETED call's answer */
    inFlight: boolean;
  } | null;
  limits: WebhookLimits;
  schema: WebhookSchemaInfo;
}

export interface DeliveryLogEntry {
  deliveryId: string;
  productId: string;
  runId: string;
  runSeq: number;
  runKind: 'full_export' | 'changes';
  event: 'product.updated' | 'product.full_export';
  origin: 'production' | 'resend' | 'test' | 'recheck';
  status: CallStatus;
  attempts: number;
  calls: number;
  httpStatus: number | null;
  durationMs: number | null;
  outcome: string | null;
  response: string | null;
  error: string | null;
  heldPath?: string | null;
  schemaRevision?: string | null;
  location?: string | null;
  retryAfter?: string | null;
  renderSource?: 'primary' | 'replica' | null;
  payloadHash: string | null;
  bytes: number | null;
  lastCallAt: string | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  updatedAt: string | null;
  canResend: boolean;
}

export type AuthInput =
  | { type: 'header'; name: string; value: string }
  | { type: 'basic'; username: string; password: string };

export class WebhookApiError extends Error {
  status: number;
  code: string | null;
  field: string | null;

  constructor(status: number, message: string, code: string | null, field: string | null) {
    super(message);
    this.name = 'WebhookApiError';
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

async function call<T = { webhook: WebhookDelivery }>(path: string, init: RequestInit = {}, signal?: AbortSignal): Promise<T> {
  const response = await authFetch(path, { ...init, signal });
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const message = (body && typeof body.error === 'string' && body.error) || `Request failed with HTTP ${response.status}`;
    throw new WebhookApiError(response.status, message, body && body.code ? String(body.code) : null, body && body.field ? String(body.field) : null);
  }
  return body;
}

const base = (siteId: string) => `/api/sites/${siteId}/webhook-delivery`;

/** A download (rev. 5.3): the exact bytes, or the server's sentence as a WebhookApiError. */
async function download(path: string): Promise<ArrayBuffer> {
  const response = await authFetch(path);
  if (!response.ok) {
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const message = (body && typeof body.error === 'string' && body.error) || `Request failed with HTTP ${response.status}`;
    throw new WebhookApiError(response.status, message, body && body.code ? String(body.code) : null, null);
  }
  return response.arrayBuffer();
}

/** Hand a file to the browser to save. */
export function saveFile(bytes: ArrayBuffer | string, fileName: string, type: string): void {
  const url = URL.createObjectURL(new Blob([bytes], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const webhookDeliveryApi = {
  get: (siteId: string, signal?: AbortSignal) => call(base(siteId), {}, signal),
  // rev. 5.6: canonicalsOnly is sent only where the option exists (left out, the backend reads false)
  enable: (siteId: string, url: string, auth: AuthInput, canonicalsOnly?: boolean) => call(`${base(siteId)}/enable`,
    json('POST', canonicalsOnly === undefined ? { url, auth } : { url, auth, canonicalsOnly })),
  updateUrl: (siteId: string, url: string) => call(`${base(siteId)}/url`, json('PUT', { url })),
  replaceAuth: (siteId: string, auth: AuthInput) => call(`${base(siteId)}/auth`, json('PUT', { auth })),
  setPace: (siteId: string, maxParallel: number, maxPerMinute: number) => call(`${base(siteId)}/pace`, json('PUT', { maxParallel, maxPerMinute })),
  setTimeout: (siteId: string, requestTimeoutSeconds: number) => call(`${base(siteId)}/timeout`, json('PUT', { requestTimeoutSeconds })),
  setGzip: (siteId: string, enabled: boolean) => call(`${base(siteId)}/gzip`, json('PUT', { enabled })),
  pause: (siteId: string) => call(`${base(siteId)}/pause`, { method: 'POST' }),
  resume: (siteId: string) => call(`${base(siteId)}/resume`, { method: 'POST' }),
  disable: (siteId: string) => call(`${base(siteId)}/disable`, { method: 'POST' }),
  requestFullExport: (siteId: string) => call(`${base(siteId)}/full-export`, { method: 'POST' }),
  sendTest: (siteId: string) => call<{ webhook: WebhookDelivery; deliveryId: string; productId: string }>(`${base(siteId)}/test`, { method: 'POST' }),
  deliveries: (siteId: string, { filter = 'all', cursor = null }: { filter?: LogFilter; cursor?: string | null } = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ filter });
    if (cursor) qs.set('cursor', cursor);
    return call<{ deliveries: DeliveryLogEntry[]; nextCursor: string | null }>(`${base(siteId)}/deliveries?${qs.toString()}`, {}, signal);
  },
  resend: (siteId: string, deliveryId: string) => call<{ webhook: WebhookDelivery; deliveryId: string }>(`${base(siteId)}/deliveries/${encodeURIComponent(deliveryId)}/resend`, { method: 'POST' }),
  recheckHeld: (siteId: string) => call<{ webhook: WebhookDelivery; rechecked: number }>(`${base(siteId)}/recheck-held`, { method: 'POST' }),
  /** PRD rev. 5.3: the JSON schema of the body (the file the dispatcher validates with) */
  schema: (siteId: string) => download(`${base(siteId)}/schema`),
  /** PRD rev. 5.3: one product of the store, rendered as a call carries it; its sku names the file */
  sample: async (siteId: string): Promise<{ bytes: ArrayBuffer; sku: string }> => {
    const bytes = await download(`${base(siteId)}/sample`);
    return { bytes, sku: String(JSON.parse(new TextDecoder().decode(bytes)).sku) };
  },
  /** PRD rev. 5.4: the guide's header examples, from the same product as `sample`, without rendering it */
  sampleIdentity: async (siteId: string): Promise<WebhookSampleIdentity> => (await call<{ identity: WebhookSampleIdentity }>(`${base(siteId)}/sample-identity`)).identity,
};
